import CardTitleWithIcon from "@/components/company/card-title-with-icon";
import {
  CompanyWebProfileNotice,
  SynthesisText,
} from "@/components/company/company-web-profile-view";
import CompanyWebHighlights from "@/components/company/company-web-highlights";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useActivityTypeTranslation } from "@/hooks/useActivityTypeTranslation";
import { useTranslation } from "@/hooks/useTranslation";
import {
  hasActivityData,
  isValidEmail,
  isValidPhone,
} from "@/lib/company-data-availability";
import {
  buildCompanyBusinessLinks,
  buildCompanySocialLinks,
} from "@/lib/company-social-links";
import { cn } from "@/lib/utils";
import {
  hasWebProfileNotice,
  webHighlightsOf,
  webSourcesOf,
  webSynthesisOf,
  type CompanyWebPreviewState,
} from "@/stores/company-web-preview-store";
import { CompanyDTO } from "@shaarp/common";
import {
  BuildingIcon,
  CheckIcon,
  CopyIcon,
  ExternalLinkIcon,
  GlobeIcon,
  LinkIcon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { RiMailLine, RiPhoneLine } from "react-icons/ri";

const normalizePhoneString = (phone: string): string =>
  phone.replace(/(?!^\+)\D/g, "");

const deduplicatePhones = (
  phones: { phone: string; normalizedPhone?: string; source: string | null }[],
): { phone: string; normalizedPhone?: string; source: string | null }[] => {
  const seen = new Set<string>();
  return phones.filter((p) => {
    const key = p.normalizedPhone || normalizePhoneString(p.phone);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const deduplicateEmails = (
  emails: { email: string; source: string | null }[],
): { email: string; source: string | null }[] => {
  const seen = new Set<string>();
  return emails.filter((e) => {
    const normalized = e.email.toLowerCase().trim();
    if (seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

function ContactCopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [value]);

  return (
    <Button
      size="icon"
      variant="ghost"
      className="size-6 shrink-0"
      onClick={(e) => {
        e.stopPropagation();
        handleCopy();
      }}
    >
      {copied ? (
        <CheckIcon className="size-3 text-green-500" />
      ) : (
        <CopyIcon className="size-3 text-muted-foreground" />
      )}
    </Button>
  );
}

function ContactPopoverItem({
  value,
  href,
}: {
  value: string;
  href: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <a href={href} className="text-sm hover:underline truncate">
        {value}
      </a>
      <ContactCopyButton value={value} />
    </div>
  );
}

type CardCompanyActivityProps = {
  company: CompanyDTO;
} & (
  | {
      /**
       * The page's web run: its synthesis replaces the base's text, its
       * takeaways follow, or why it found none.
       */
      webProfile: CompanyWebPreviewState;
      /**
       * Asks the web run again after a failure a new request may fix.
       * Required with the run: without it, its failure notice is not shown.
       */
      onWebRetry: () => void;
    }
  | { webProfile?: undefined; onWebRetry?: undefined }
);

/** The activity card while its sections run and it has nothing yet. */
export function CardCompanyActivitySkeleton() {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader className="pb-3 sm:pb-4">
        <CardTitleWithIcon icon={BuildingIcon} title={t("company.insights.activity")} />
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <Skeleton variant="subtle" className="h-2 w-1/2" />
        <Skeleton variant="subtle" className="h-2 w-1/3" />
      </CardContent>
    </Card>
  );
}

export default function CardCompanyActivity({
  company,
  webProfile,
  onWebRetry,
}: CardCompanyActivityProps) {
  const { t } = useTranslation();
  const { translateActivityType } = useActivityTypeTranslation();

  const phones = useMemo(
    () =>
      deduplicatePhones(
        (company.contact.phones ?? []).filter(
          (p) => p.phone && isValidPhone(p.phone),
        ),
      ),
    [company.contact.phones],
  );

  const emails = useMemo(
    () =>
      deduplicateEmails(
        (company.contact.emails ?? []).filter(
          (e) => e.email && isValidEmail(e.email),
        ),
      ),
    [company.contact.emails],
  );

  const website = company.website;
  const domain = company.domain;

  const allLinks = [
    ...buildCompanyBusinessLinks(company),
    ...buildCompanySocialLinks(company),
  ];

  const web = webProfile
    ? webSynthesisOf(webProfile)
    : { synthesis: null, isWriting: false };
  const highlights = webProfile ? webHighlightsOf(webProfile) : [];
  const hasWebNotice = !!webProfile && hasWebProfileNotice(webProfile);
  const baseText = company.activityMain ?? company.websiteResume;

  if (
    !hasActivityData(company) &&
    !web.synthesis &&
    highlights.length === 0 &&
    !hasWebNotice
  ) {
    return null;
  }

  const hasActivity =
    !!web.synthesis || !!baseText || highlights.length > 0 || hasWebNotice;
  const hasKeyInfo =
    phones.length > 0 || emails.length > 0 || !!website || allLinks.length > 0;

  return (
    <Card>
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex items-center gap-3">
          <CardTitleWithIcon icon={BuildingIcon} title={t("company.insights.activity")} />
          {company.activityTypeMain && (
            <Badge variant="secondary">
              {translateActivityType(company.activityTypeMain)}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {web.synthesis ? (
          <SynthesisText
            text={web.synthesis}
            isWriting={web.isWriting}
            className="sm:text-base"
          />
        ) : (
          baseText && <p className="text-sm sm:text-base">{baseText}</p>
        )}

        <CompanyWebHighlights
          highlights={highlights}
          sources={webProfile ? webSourcesOf(webProfile) : []}
          isWriting={web.isWriting}
        />

        {webProfile && (
          <CompanyWebProfileNotice state={webProfile} onRetry={onWebRetry} />
        )}

        {hasKeyInfo && (
          <>
            {hasActivity && <div className="border-t" />}
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {phones.length > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <Popover>
                      <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                          <button className="flex items-center gap-2.5 transition-colors hover:text-primary">
                            <div className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-muted shrink-0">
                              <RiPhoneLine className="w-[18px] h-[18px]" />
                              {phones.length > 1 && (
                                <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center size-5 rounded-full bg-primary text-primary-foreground text-[11px] font-medium">
                                  {phones.length}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-col items-start">
                              <span className="text-[11px] text-muted-foreground leading-none">
                                {t("company.insights.keyInfo.phone")}
                              </span>
                              <span className="text-sm font-medium">
                                {phones[0].phone}
                              </span>
                            </div>
                          </button>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {t("components.dataTableCompanies.contact.phonesTooltip", {
                          count: phones.length,
                        })}
                      </TooltipContent>
                      <PopoverContent side="top" className="w-auto min-w-48 p-3">
                        <p className="text-xs text-muted-foreground mb-2">
                          {t("components.dataTableCompanies.contact.publicInfoNotice")}
                        </p>
                        <div className="space-y-1">
                          {phones.map((p, i) => (
                            <ContactPopoverItem
                              key={i}
                              value={p.phone}
                              href={`tel:${p.normalizedPhone || p.phone}`}
                            />
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </Tooltip>
                </TooltipProvider>
              )}

              {emails.length > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <Popover>
                      <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                          <button className="flex items-center gap-2.5 transition-colors hover:text-primary">
                            <div className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-muted shrink-0">
                              <RiMailLine className="w-[18px] h-[18px]" />
                              {emails.length > 1 && (
                                <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center size-5 rounded-full bg-primary text-primary-foreground text-[11px] font-medium">
                                  {emails.length}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-col items-start">
                              <span className="text-[11px] text-muted-foreground leading-none">
                                {t("company.insights.keyInfo.email")}
                              </span>
                              <span className="text-sm font-medium">
                                {emails[0].email}
                              </span>
                            </div>
                          </button>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {t("components.dataTableCompanies.contact.emailsTooltip", {
                          count: emails.length,
                        })}
                      </TooltipContent>
                      <PopoverContent side="top" className="w-auto min-w-48 p-3">
                        <p className="text-xs text-muted-foreground mb-2">
                          {t("components.dataTableCompanies.contact.publicInfoNotice")}
                        </p>
                        <div className="space-y-1">
                          {emails.map((e, i) => (
                            <ContactPopoverItem
                              key={i}
                              value={e.email}
                              href={`mailto:${e.email}`}
                            />
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </Tooltip>
                </TooltipProvider>
              )}

              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 transition-colors hover:text-primary"
                >
                  <GlobeIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-[11px] text-muted-foreground leading-none">
                      {t("company.insights.keyInfo.website")}
                    </span>
                    <span className="text-sm font-medium">
                      {domain ?? website}
                    </span>
                  </div>
                  <ExternalLinkIcon className="w-3 h-3 text-muted-foreground shrink-0" />
                </a>
              )}

              {allLinks.length > 0 && (
                <div className="flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] text-muted-foreground leading-none">
                      {t("company.insights.keyInfo.links")}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <TooltipProvider>
                        {allLinks.map((link) => (
                          <Tooltip key={link.url}>
                            <TooltipTrigger asChild>
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center justify-center w-7 h-7 transition-colors rounded-md hover:bg-muted"
                              >
                                <link.icon
                                  className={cn(
                                    "w-5 h-5",
                                    link.colorClass || "text-muted-foreground",
                                  )}
                                />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {link.label}
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
